/**
 * On-chain balances arrive as integer strings in the token's smallest unit (wei).
 * Stablecoins like USDT/USDC use 6 decimals; everything else defaults to 18.
 */
export function decimalsForSymbol(symbol?: string): number {
  const s = (symbol ?? "").toLowerCase();
  if (s === "usdt" || s === "usdc") return 6;
  return 18;
}

/**
 * Minimal local reimplementation of ethers' formatUnits. This file used to
 * `import { formatUnits } from "ethers"` — a static import that pulled the
 * entire ethers ESM bundle into the Navbar client-chunk graph (Navbar →
 * WalletBalance → useUserBalance → here). Turbopack's dev server 404'd that
 * ethers chunk, and for logged-in users (WalletBalance actually rendering)
 * the resulting ChunkLoadError crashed the page into an endless reload loop.
 * Everything else uses ethers only via lazy `await import("ethers")` inside
 * click handlers, which must stay that way — never import ethers statically
 * from any module the layout/navbar reaches.
 */
function formatUnitsLocal(value: bigint, decimals: number): string {
  const negative = value < BigInt(0);
  const abs = negative ? -value : value;
  const base = BigInt(10) ** BigInt(decimals);
  const whole = (abs / base).toString();
  const frac = (abs % base).toString().padStart(decimals, "0").replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${frac ? `.${frac}` : ""}`;
}

/**
 * The same conversion as `toTokenAmount`, but kept as an exact decimal STRING.
 *
 * Phase 08a: `toTokenAmount` returns a `number`, and an 18-decimal balance
 * passes Number.MAX_SAFE_INTEGER at about 0.009 of a whole token — fine for the
 * navbar's rounded display, wrong for comparing a withdrawal amount against a
 * balance. The transaction flows use this; every existing caller of
 * `toTokenAmount` is untouched.
 */
export function toTokenAmountString(raw: string | number | null | undefined, symbol?: string): string | null {
  if (raw === null || raw === undefined) return null;
  try {
    const digits = String(raw).split(".")[0];
    if (!/^-?\d+$/.test(digits)) return null;
    return formatUnitsLocal(BigInt(digits), decimalsForSymbol(symbol));
  } catch {
    return null;
  }
}

/** Convert a raw on-chain balance to a human-readable number. */
export function toTokenAmount(raw: string | number, symbol?: string): number {
  try {
    const value = formatUnitsLocal(BigInt(String(raw ?? "0").split(".")[0] || "0"), decimalsForSymbol(symbol));
    return Number(value);
  } catch {
    return 0;
  }
}

/** Format a token amount with trimmed, locale-aware decimals. */
export function formatTokenAmount(raw: string | number, symbol?: string, locale: "fa" | "en" = "en"): string {
  const amount = toTokenAmount(raw, symbol);
  if (amount === 0) return "0";
  const maximumFractionDigits = amount < 1 ? 6 : 4;
  return amount.toLocaleString(locale === "fa" ? "fa-IR" : "en-US", { maximumFractionDigits });
}

/**
 * WHAT THE HOLDER CAN USE NOW, in whole tokens. A bid or an offer holds part of
 * a balance (`locked`); the backend states the rest as `spendable` (a
 * whole-token decimal, not wei). Every figure that answers "how much do I
 * have" — the wallet card, the header menu, the asset list, the bid's and the
 * withdrawal's balance checks — reads this, so 20 POL with 10 in a bid reads
 * 10, not 20; the locked part is said beside it. Without `spendable` (an older
 * response) it is the whole balance, as before.
 */
export function usableTokens(asset: { balance: string | number; symbol?: string; spendable?: string | null }): number {
  if (asset.spendable !== undefined && asset.spendable !== null && String(asset.spendable).trim() !== "") {
    const n = Number(asset.spendable);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return toTokenAmount(asset.balance, asset.symbol);
}

/** `usableTokens` as an exact decimal string (for the balance checks), or null when unusable. */
export function usableTokensString(asset: { balance: string | number; symbol?: string; spendable?: string | null }): string | null {
  const s = asset.spendable === undefined || asset.spendable === null ? "" : String(asset.spendable).trim();
  if (/^\d+(\.\d+)?$/.test(s)) return s.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
  return toTokenAmountString(asset.balance, asset.symbol);
}

/** The usable part's value at the sell price (toman). */
export function usableIrr(asset: { balance: string | number; symbol?: string; spendable?: string | null; price_sell_irr: string | number }): number {
  return usableTokens(asset) * (parseFloat(String(asset.price_sell_irr || "0")) || 0);
}

/** A whole-token number in the page's digits — `formatTokenAmount`'s rule, for an amount already in tokens. */
export function formatTokens(amount: number, locale: "fa" | "en" = "en"): string {
  if (amount === 0) return "0";
  return amount.toLocaleString(locale === "fa" ? "fa-IR" : "en-US", { maximumFractionDigits: amount < 1 ? 6 : 4 });
}
